import { useCallback } from "react";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { ATTACHMENT_ONLY_PROMPT } from "@/app/lib/constants";
import { METHOD_TYPE, MILVUS_ACTIONS } from "@/app/lib/enums";
import { logger } from "@/app/lib/logger";
import { getBrowserTimeZone } from "@/app/lib/temporalContext";
import { useStateHistoryChatPage } from "@/app/store/chat";
import type { ChatMessage, ExpandedQueryResponse, PlaceholderRegistry } from "@/types";
import {
   CHAT_TIMEOUT_MS,
   HISTORY_SUMMARY_PENDING_MAX_ATTEMPTS,
   INPUT_FOCUS_DELAY_MS,
   TIMEOUT_CHECK_INTERVAL_MS
} from "./constants";
import {
   buildHistoryFromStreamPayload,
   findLastAssistantContent,
   mergeFeedbackIntoHistory
} from "./historyMappers";
import {
   extractSseMessages,
   parseRecoverableStreamData,
   readSseDataLine,
   trimProcessedSseBuffer
} from "./streamParsing";
import type { HistoryViewGuard } from "./useHistoryViewGuard";
import type {
   ChatMetadata,
   ChatTranslation,
   InputRefFocusAfterFinishChat,
   SaveResponseToHistory,
   SetChatError,
   SetCurrentAttachments,
   SetHistory,
   SetHistoryItem,
   SetLoading,
   SetStreamingResponse,
   StopChatStreaming,
   StreamRefs,
   UpdateActiveGeneration
} from "./types";

const USAGE_REFRESH_DELAY_MS = 1_000;

interface UseChatSubmissionOptions extends Pick<
   StreamRefs,
   | "currentReader"
   | "timeoutRef"
   | "streamingHistoryRef"
   | "isSavingRef"
   | "isUnmountingRef"
   | "abortControllerRef"
> {
   history: ChatMessage[];
   setHistory: SetHistory;
   selectedModel: string;
   selectedTemperature: number;
   selectedStreaming: boolean;
   selectedBaseKnowledgeId: string;
   isTemporaryChat: boolean;
   historyItemId?: string;
   shareKey?: string;
   checkAuth: (options?: { forceRefresh?: boolean }) => Promise<unknown>;
   translation: ChatTranslation;
   addActiveGeneration: UpdateActiveGeneration;
   saveResponseToHistory: SaveResponseToHistory;
   stopChatStreaming: StopChatStreaming;
   setLoading: SetLoading;
   setError: SetChatError;
   triggerHistoryRefetch: () => void;
   triggerUsageRefresh: (projectId: string) => void;
   locale: string;
   studentChatHistoryId?: string;
   removeActiveGeneration: UpdateActiveGeneration;
   inputRefFocusAfterFinishChat?: InputRefFocusAfterFinishChat;
   setHistoryItem: SetHistoryItem;
   setCurrentAttachments: SetCurrentAttachments;
   setStreamingResponse: SetStreamingResponse;
   markCurrentChatUpdated?: () => void;
   viewGuard: HistoryViewGuard;
   newChatIdAssignedRef: React.MutableRefObject<string | null>;
}

export function useChatSubmission({
   history,
   setHistory,
   selectedModel,
   selectedTemperature,
   selectedStreaming,
   selectedBaseKnowledgeId,
   isTemporaryChat,
   historyItemId,
   shareKey,
   checkAuth,
   translation,
   addActiveGeneration,
   saveResponseToHistory,
   stopChatStreaming,
   setLoading,
   setError,
   triggerHistoryRefetch,
   triggerUsageRefresh,
   locale,
   studentChatHistoryId,
   removeActiveGeneration,
   inputRefFocusAfterFinishChat,
   setHistoryItem,
   setCurrentAttachments,
   setStreamingResponse,
   viewGuard,
   newChatIdAssignedRef,
   currentReader,
   timeoutRef,
   streamingHistoryRef,
   isSavingRef,
   isUnmountingRef,
   abortControllerRef,
   markCurrentChatUpdated
}: UseChatSubmissionOptions) {
   return useCallback(
      async (text: string = "") => {
         // Read at send time rather than through props: staging a file must not
         // re-create this callback mid-stream.
         const { pendingAttachments, clearPendingAttachments } =
            useStateHistoryChatPage.getState();
         const readyAttachments = pendingAttachments.filter(
            (item) => item.status === "ready" && item.fileId
         );
         const attachmentIds = readyAttachments.map((item) => item.fileId as string);
         const attachedFilesForTurn = readyAttachments.map((item) => ({
            id: item.fileId as string,
            name: item.name,
            mimetype: item.mimetype,
            size: item.size,
            url: api_url.file_url.replace("{id}", item.fileId as string)
         }));

         if (!text.trim() && !attachmentIds.length) return;

         // Capture the visible view so late writes are dropped once the user
         // switches chats mid-stream.
         const scope = viewGuard.capture();
         let chatHistoryId = historyItemId;

         // Attaching a file with no text is a normal way to ask "what is this?";
         // the stand-in gives retrieval something to work with.
         const textClean = text.trim() || ATTACHMENT_ONLY_PROMPT;
         const lastEntry = history[history.length - 1];
         const isSameUserMessage =
            lastEntry?.role === "user" && lastEntry.content === textClean;
         const previousFiles = lastEntry?.attachedFiles ?? [];
         const hasNothingStaged = !attachmentIds.length;
         const previousFileIds = new Set(previousFiles.map((file) => file.id));
         const stagedFileIds = new Set(attachmentIds);
         const hasSameStagedFiles =
            stagedFileIds.size === previousFileIds.size &&
            [...previousFileIds].every((id) => stagedFileIds.has(id));
         const isResendOfUnanswered =
            isSameUserMessage && (hasNothingStaged || hasSameStagedFiles);
         const turnFiles =
            isResendOfUnanswered && hasNothingStaged
               ? previousFiles
               : attachedFilesForTurn;
         const newHistory = [
            ...(isResendOfUnanswered ? history.slice(0, -1) : history),
            { role: "user", content: textClean, attachedFiles: turnFiles }
         ] as ChatMessage[];

         let installedController: AbortController | undefined = undefined;
         for (
            let attempt = 1;
            attempt <= HISTORY_SUMMARY_PENDING_MAX_ATTEMPTS;
            attempt++
         ) {
            if (attempt > 1 && abortControllerRef.current !== installedController) return;
            let finalResponse = "";
            let finalExpandedQuery: ExpandedQueryResponse | undefined = undefined;
            let finalMetaData: ChatMetadata = [];
            let finalPlaceholderRegistry: PlaceholderRegistry | undefined = undefined;
            let buffer = "";
            let serverPersistFailed = false;
            let signal: AbortSignal | undefined = undefined;
            let generationIdToCleanup: string | null = null;
            let shouldRetry = false;

            try {
               if (abortControllerRef.current) {
                  abortControllerRef.current.abort();
               }
               installedController = new AbortController();
               abortControllerRef.current = installedController;
               signal = installedController.signal;

               setLoading(true);
               setStreamingResponse("");
               setError(null);
               isSavingRef.current = false;

               await stopChatStreaming();
               streamingHistoryRef.current = null;

               scope.run(() => setHistory(newHistory));

               const generationId = chatHistoryId;
               if (generationId) {
                  addActiveGeneration(generationId);
                  generationIdToCleanup = generationId;
                  logger.info("[ChatStream] Registered active generation (pre-fetch)", {
                     generationId
                  });
               }

               const response = await fetchWithAuth(api_url.milvus_url, {
                  method: METHOD_TYPE.POST,
                  body: JSON.stringify({
                     type: MILVUS_ACTIONS.CHAT_VECTOR,
                     query: textClean,
                     history: newHistory,
                     modelName: selectedModel,
                     temperature: selectedTemperature,
                     streaming: selectedStreaming,
                     projectName: selectedBaseKnowledgeId,
                     isReason: false,
                     isTemporary: isTemporaryChat,
                     chatHistoryId: chatHistoryId || null,
                     shareKey: shareKey || null,
                     timeZone: getBrowserTimeZone(),
                     knowledgeBase: [selectedBaseKnowledgeId],
                     attachmentIds: turnFiles.map((file) => file.id)
                  }),
                  headers: {
                     "Content-Type": "application/json"
                  },
                  signal
               });

               // Only once the turn is under way: clearing earlier would strand the staged
               // uploads on an immediate failure, leaving nothing to retry with.
               if (response.ok && response.body) clearPendingAttachments();

               if (!response.ok || !response.body) {
                  if (response.status === 403) {
                     const errorData = await response.json().catch(() => ({}));
                     await checkAuth({ forceRefresh: true });
                     const errObj = {
                        message:
                           errorData.message ||
                           errorData.error ||
                           translation("notAuthorizedKnowledgeBase"),
                        code: errorData.code || "ACCESS_DENIED",
                        status: 403
                     };
                     throw new Error(JSON.stringify(errObj));
                  }
                  if (response.status === 404) {
                     const errObj = {
                        message: translation("serverNotFound"),
                        code: "SERVER_NOT_FOUND",
                        status: 404
                     };
                     throw new Error(JSON.stringify(errObj));
                  }
                  if (response.status === 429) {
                     const errorData = await response.json().catch(() => ({}));
                     const errorMessage =
                        errorData.error || translation("monthlyUsageLimitExceeded");
                     const errObj = {
                        message: errorMessage,
                        code: errorData.code || "RATE_LIMIT_EXCEEDED",
                        status: 429
                     };
                     throw new Error(JSON.stringify(errObj));
                  }
                  if (response.status === 400) {
                     const errorData = await response.json().catch(() => ({}));
                     const errorMessage =
                        errorData.message ||
                        response.statusText ||
                        translation("unableToConnect");
                     const errObj = {
                        message: errorMessage,
                        code: errorData.code || "BAD_REQUEST",
                        status: 400
                     };
                     throw new Error(JSON.stringify(errObj));
                  }
                  let errorData: any = {};
                  try {
                     errorData = await response.json();
                  } catch (e) {
                     errorData = {};
                  }
                  const errorMessage =
                     errorData.error ||
                     errorData.message ||
                     response.statusText ||
                     translation("unableToConnect");
                  const errObj = {
                     message: errorMessage,
                     code: errorData.code || "UNKNOWN_ERROR",
                     status: response.status
                  };
                  throw new Error(JSON.stringify(errObj));
               }

               currentReader.current = response.body.getReader();
               const reader = currentReader.current;
               const decoder = new TextDecoder("utf-8", { fatal: false });
               let lastActivity = Date.now();

               timeoutRef.current = setInterval(() => {
                  const inactiveTime = Date.now() - lastActivity;
                  if (inactiveTime > CHAT_TIMEOUT_MS) {
                     if (timeoutRef.current) clearInterval(timeoutRef.current);
                     timeoutRef.current = null;
                     saveResponseToHistory(finalResponse, { scope });
                     setError(translation("requestTimeout"));
                     setLoading(false);
                     reader.cancel();
                  }
               }, TIMEOUT_CHECK_INTERVAL_MS);

               try {
                  streamingHistoryRef.current = chatHistoryId || null;
                  processStream: while (true) {
                     const { done, value } = await reader.read();

                     if (done) {
                        saveResponseToHistory(finalResponse, { scope });
                        break;
                     }

                     lastActivity = Date.now();
                     const chunk = decoder.decode(value, { stream: true });
                     buffer += chunk;

                     const messages = extractSseMessages(buffer);

                     if (messages.length > 0) {
                        buffer = trimProcessedSseBuffer(buffer);

                        for (const line of messages) {
                           const json = readSseDataLine(line);
                           if (!json) continue;

                           try {
                              const data = parseRecoverableStreamData(json);
                              if (!data) continue;

                              if (data.type === "heartbeat") {
                                 lastActivity = Date.now();
                                 continue;
                              }

                              if (data.type === "saved") {
                                 logger.info("[ChatStream] Server persisted chat", {
                                    chatHistoryId: data.chatHistoryId,
                                    isNewChat: data.isNewChat
                                 });
                                 if (data.chatHistoryId) {
                                    addActiveGeneration(data.chatHistoryId);
                                    chatHistoryId = data.chatHistoryId;
                                    generationIdToCleanup = data.chatHistoryId;
                                    streamingHistoryRef.current = data.chatHistoryId;
                                    if (data.isNewChat && !historyItemId) {
                                       // Only adopt the new chat id / URL while this
                                       // chat is still the visible view; otherwise the
                                       // switch the user just made would be hijacked.
                                       scope.run(() => {
                                          streamingHistoryRef.current =
                                             data.chatHistoryId!;
                                          // Mark so the history loader treats the
                                          // upcoming activeHistoryId change as an id
                                          // assignment (not a real chat switch).
                                          newChatIdAssignedRef.current =
                                             data.chatHistoryId!;
                                          setHistoryItem({
                                             id: data.chatHistoryId
                                          } as any);
                                          window.history.replaceState(
                                             null,
                                             "",
                                             `/${locale}/c/${data.chatHistoryId}`
                                          );
                                          logger.info(
                                             "[ChatStream] New chat: sidebar refreshed & URL updated",
                                             { chatHistoryId: data.chatHistoryId }
                                          );
                                       });
                                       triggerHistoryRefetch();
                                    }
                                 }
                                 continue;
                              }

                              if (data.type === "persist-error") {
                                 logger.warn("[ChatStream] Server persist failed", {
                                    phase: data.phase,
                                    message: data.message
                                 });
                                 serverPersistFailed = true;
                                 continue;
                              }

                              if (data.token) {
                                 scope.run(() =>
                                    setStreamingResponse((prev) => prev + data.token)
                                 );
                                 finalResponse += data.token;
                              } else if (
                                 data.type === "complete" &&
                                 data.status === "success"
                              ) {
                                 const updatedHistory =
                                    buildHistoryFromStreamPayload(data);
                                 const mergedHistory = mergeFeedbackIntoHistory(
                                    updatedHistory!,
                                    history
                                 );

                                 finalExpandedQuery = data?.expandedQuery;
                                 finalMetaData = data?.functionCallsUsed || [];
                                 finalPlaceholderRegistry = data?.placeholderRegistry;
                                 scope.run(() => {
                                    setCurrentAttachments(data.attachments);
                                    setHistory(mergedHistory);
                                    setStreamingResponse("");
                                    if (!serverPersistFailed) {
                                       markCurrentChatUpdated?.();
                                    }
                                 });

                                 const lastAssistantContent = findLastAssistantContent(
                                    data?.history
                                 );
                                 if (lastAssistantContent) {
                                    finalResponse = lastAssistantContent;
                                 }
                              } else if (data.type === "done") {
                                 setTimeout(() => {
                                    triggerUsageRefresh(selectedBaseKnowledgeId);
                                 }, USAGE_REFRESH_DELAY_MS);

                                 const saveOptions = {
                                    expandedQuery: finalExpandedQuery,
                                    metaData: finalMetaData,
                                    placeholderRegistry: finalPlaceholderRegistry,
                                    scope
                                 };
                                 if (serverPersistFailed || isTemporaryChat) {
                                    await saveResponseToHistory(finalResponse, {
                                       ...saveOptions,
                                       persistToServer: true
                                    });
                                 } else {
                                    await saveResponseToHistory(finalResponse, {
                                       ...saveOptions,
                                       persistToServer: false
                                    });
                                    isSavingRef.current = true;
                                 }
                                 await stopChatStreaming(finalResponse, scope);
                                 setLoading(false);
                                 break processStream;
                              } else if (data.status === "error") {
                                 throw new Error(
                                    data.message || translation("undefinedError")
                                 );
                              }
                           } catch (parseError) {
                              logger.error(
                                 "[ChatStream] Error parsing response:",
                                 parseError
                              );
                              if (
                                 parseError instanceof Error &&
                                 parseError.message.includes("HISTORY_SUMMARY_PENDING")
                              )
                                 throw parseError;
                              throw new Error(translation("processingError"));
                           }
                        }
                     }
                  }
               } finally {
                  if (finalResponse && !isSavingRef.current) {
                     await saveResponseToHistory(finalResponse, {
                        expandedQuery: finalExpandedQuery,
                        metaData: finalMetaData,
                        placeholderRegistry: finalPlaceholderRegistry,
                        persistToServer: serverPersistFailed || isTemporaryChat,
                        scope
                     });
                  }
                  isSavingRef.current = false;
               }
            } catch (error) {
               if (error instanceof Error && error.name === "AbortError") {
                  logger.debug("[ChatStream] Stream request aborted successfully.");
                  return;
               }
               const isHistorySummaryPending =
                  error instanceof Error &&
                  error.message.includes("HISTORY_SUMMARY_PENDING");
               if (
                  isHistorySummaryPending &&
                  attempt < HISTORY_SUMMARY_PENDING_MAX_ATTEMPTS
               ) {
                  shouldRetry = true;
                  continue;
               }
               const errorMessage = isHistorySummaryPending
                  ? translation("historySummaryPending")
                  : error instanceof Error
                    ? error.message
                    : translation("unknownError");
               setError(errorMessage);
               setLoading(false);
               if (finalResponse && !isSavingRef.current) {
                  await saveResponseToHistory(finalResponse, { scope });
               }
            } finally {
               if (!shouldRetry) {
                  const cleanupId =
                     generationIdToCleanup || historyItemId || studentChatHistoryId;
                  if (cleanupId && !isUnmountingRef.current) {
                     if (signal?.aborted) {
                        logger.debug(
                           "[ChatStream] Skipping removeActiveGeneration (cancelled by abort)",
                           { cleanupId }
                        );
                     } else {
                        removeActiveGeneration(cleanupId);
                        logger.debug(
                           "[ChatStream] Removed active generation in finally",
                           {
                              cleanupId
                           }
                        );
                     }
                  }
                  setTimeout(
                     () => inputRefFocusAfterFinishChat?.current?.focus(),
                     INPUT_FOCUS_DELAY_MS
                  );
                  if (signal?.aborted) {
                     logger.debug(
                        "[ChatStream] Skipping setLoading(false) due to abortion or chat switch"
                     );
                  } else {
                     setLoading(false);
                  }
               }
            }
            break;
         }
      },
      [
         history,
         setHistory,
         selectedModel,
         selectedTemperature,
         selectedStreaming,
         selectedBaseKnowledgeId,
         isTemporaryChat,
         historyItemId,
         shareKey,
         checkAuth,
         translation,
         addActiveGeneration,
         saveResponseToHistory,
         stopChatStreaming,
         setLoading,
         setError,
         triggerHistoryRefetch,
         triggerUsageRefresh,
         locale,
         studentChatHistoryId,
         removeActiveGeneration,
         inputRefFocusAfterFinishChat,
         setHistoryItem,
         setCurrentAttachments,
         setStreamingResponse,
         viewGuard,
         newChatIdAssignedRef,
         markCurrentChatUpdated
      ]
   );
}
